/**
 * CategoryHeader Component
 * 
 * Features:
 * - Title centered absolutely (left: 50% + translateX)
 * - Back button pinned to inline-start
 * - Maintains symmetry in RTL
 * - No layout shift
 * 
 * Props:
 * - icon: emoji/icon string for the category
 * - title: { en: string, ar: string } - localized title
 * - subtitle: { en: string, ar: string } - optional subtitle (not used in centered layout)
 * - onBack: optional custom back handler
 * - showLevel: boolean - whether to show level selector (default: false for centered design)
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasInAppHistory } from '../../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import './CategoryHeader.css';

export default function CategoryHeader({
  icon,
  title,
  onBack,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const dir = isRTL ? 'rtl' : 'ltr';
  
  // Handle back navigation
  const handleBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else if (hasInAppHistory()) {
      navigate(-1);
    } else {
      navigate('/student-hub', { replace: true });
    }
  }, [onBack, navigate]);
  
  return (
    <header className="catHeader" dir={dir} data-testid="category-header">
      {/* Back Button - pinned to inline-start */}
      <button 
        className="catBackBtn" 
        onClick={handleBack} 
        data-testid="back-btn"
        aria-label={t('catehead.goBack')}
      >
        <ArrowLeft size={20} className={isRTL ? 'rtl-flip' : ''} />
      </button>
      
      {/* Title - absolutely centered */}
      <h1 className="catTitle" data-testid="category-title">
        {icon && <span className="catIcon">{icon}</span>}
        <span>{isRTL ? title.ar : title.en}</span>
      </h1>
    </header>
  );
}
